import { useRef, useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDayYear } from '../../lib/time';
import { showConfirm } from '../../lib/telegram';
import { CloseIcon } from '../../ui/icons';
import { Notice, Pill } from '../../ui/parts';
import { useAddSuperAdmin, useSuperAdminMutations, useSuperAdmins, useTenants, type SuperAdmin } from '../api';

/** Вкладка «Платформа»: ваш бот, сводка по организациям и владельцы продукта. */
export function Platform() {
  const sheet = useSheet();
  const data = useTenants();
  const admins = useSuperAdmins();

  useScreen('Платформа', null);

  const list = data.data?.tenants ?? [];
  const waiting = list.filter((t) => t.pendingInvite).length;
  const botless = list.filter((t) => !t.bot.connected).length;
  const owners = admins.data ?? [];

  return (
    <Screen>
      <p className="lead">
        Управляет подключением организаций к сервису и предоставляет владельцам продукта доступ к
        администрированию подключённых организаций.
      </p>

      <div className="kv">
        <div>
          <span className="k">Бот платформы</span>
          <span className="v">{data.data?.platformBot ? `@${data.data.platformBot}` : 'не подключён'}</span>
        </div>
        <div>
          <span className="k">Подключено организаций</span>
          <span className="v">{list.length}</span>
        </div>
        <div>
          <span className="k">Ожидают подтверждения</span>
          <span className="v">{waiting}</span>
        </div>
        <div>
          <span className="k">Не настроили собственного бота</span>
          <span className="v">{botless}</span>
        </div>
      </div>

      {data.data && !data.data.platformBot ? (
        <Notice>
          У платформы нет бота, поэтому ссылки-приглашения собрать не из чего. Подключите бота
          платформенной организации — и приглашения заработают.
        </Notice>
      ) : null}

      <p className="eyebrow" style={{ marginTop: 4 }}>Владельцы продукта</p>
      <div className="rows">
        {owners.map((a) => (
          <OwnerRow key={a.tg_id} owner={a} onOpen={() => sheet.open(<OwnerSheet owner={a} />)} />
        ))}
        {admins.data && admins.data.length === 0 ? <p className="lead">Владельцев нет.</p> : null}
      </div>

      <button className="add" onClick={() => sheet.open(<AddSuperAdminSheet />)}>＋ Добавить владельца</button>

      <Notice>
        Владелец продукта имеет доступ ко всем организациям платформы и может входить в любую из них
        с правами администратора. Все такие входы фиксируются в журнале действий организации.
      </Notice>
    </Screen>
  );
}

/**
 * Строка владельца. Своя строка не нажимается: права с себя не снимают —
 * сняв их, человек в ту же секунду теряет экран, на котором стоит. Это же
 * правило стоит на сервере (`SELF_OWNER`), кнопка лишь не обещает лишнего.
 */
function OwnerRow({ owner, onOpen }: { owner: SuperAdmin; onOpen: () => void }) {
  const body = (
    <>
      <span className="rw-body">
        <b>{owner.name || `id ${owner.tg_id}`}</b>
        <span>tg id {owner.tg_id}</span>
      </span>
      {owner.isMe
        ? <Pill kind="paid">вы</Pill>
        : owner.suspended_at
          ? <Pill kind="off">приостановлен</Pill>
          : <Pill kind="paid">владелец</Pill>}
    </>
  );
  return owner.isMe
    ? <div className="rw">{body}</div>
    : <button className="rw" type="button" onClick={onOpen}>{body}</button>;
}

/**
 * Что можно сделать с чужой строкой: приостановить доступ или убрать человека
 * из владельцев.
 *
 * Два разных действия, а не одно. Приостановка обратима одним нажатием;
 * удаление снимает только роль — аккаунт, его карточки в организациях и всё,
 * что он там делал, остаются, — но вернуть роль можно будет лишь заново, по
 * Telegram ID, поэтому оно спрашивает подтверждение.
 *
 * Порядок сверху вниз — тот, в котором на панель смотрят: кто это, что у него
 * с доступом, что будет от нажатия, само нажатие, и только потом удаление.
 */
function OwnerSheet({ owner }: { owner: SuperAdmin }) {
  const sheet = useSheet();
  const toast = useToast();
  const tz = useMeContext().center.tz;
  const { suspend, remove } = useSuperAdminMutations();

  const busy = suspend.isPending || remove.isPending;
  const suspended = !!owner.suspended_at;
  const fail = (e: unknown, fallback: string) => toast(e instanceof ApiError ? e.message : fallback);

  async function del() {
    const ok = await showConfirm(
      `Убрать «${owner.name || owner.tg_id}» из владельцев продукта? Аккаунт останется, вернуть роль можно будет только заново, по Telegram ID.`,
      { title: 'Удалить из владельцев', destructive: true },
    );
    if (!ok) return;
    remove.mutate(owner.tg_id, {
      onSuccess: () => { toast('Удалён из владельцев'); sheet.close(); },
      onError: (e) => fail(e, 'Не получилось удалить'),
    });
  }

  return (
    <div className="owner-panel">
      <div className="sheet-head">
        {/* Имени может не быть — тогда заголовок называет роль, а идентификатор
            уходит в строку под ним и остаётся читаемым. */}
        <h3>{owner.name || 'Владелец продукта'}</h3>
        <button className="sheet-close" type="button" aria-label="Закрыть" onClick={() => sheet.close()}>
          <CloseIcon />
        </button>
      </div>

      <p className="owner-id">
        {owner.username ? <b>@{owner.username}</b> : null}
        <span>Telegram ID {owner.tg_id}</span>
      </p>

      {/* Состояние — словами, а не одним цветом: цвет читают не все и не всегда. */}
      <div className="kv kv-wrap">
        <div className="kv-row">
          <span className="k">Доступ</span>
          <span className="v">{suspended ? 'Приостановлен' : 'Активен'}</span>
        </div>
        <div className="kv-row">
          <span className="k">Добавлен</span>
          <span className="v">{formatDayYear(owner.created_at, tz)}</span>
        </div>
        {suspended ? (
          <div className="kv-row">
            <span className="k">Приостановлен</span>
            <span className="v">{formatDayYear(owner.suspended_at!, tz)}</span>
          </div>
        ) : null}
      </div>

      <p>
        {suspended
          ? 'Доступ к управлению платформой и организациями отключён. Вы можете восстановить его.'
          : 'Приостановка отключит доступ к управлению платформой и организациями. Доступ можно восстановить.'}
      </p>

      <button
        className={suspended ? 'btn solid' : 'btn'}
        type="button"
        disabled={busy}
        onClick={() => suspend.mutate({ tgId: owner.tg_id, suspended: !suspended }, {
          onSuccess: () => { toast(suspended ? 'Доступ восстановлен' : 'Доступ приостановлен'); sheet.close(); },
          onError: (e) => fail(e, 'Не получилось изменить доступ'),
        })}
      >
        {suspended ? 'Восстановить доступ' : 'Приостановить доступ'}
      </button>

      {/* Последнего действующего владельца сервер не отдаст (`LAST_OWNER`), но
          из интерфейса до этого не дойти: своя строка не нажимается, а значит
          хотя бы один действующий владелец — тот, кто сейчас смотрит на экран. */}
      <div className="danger-zone">
        <button className="linkish danger" type="button" disabled={busy} onClick={() => void del()}>
          Удалить из владельцев
        </button>
      </div>
    </div>
  );
}

/**
 * Добавление владельца продукта.
 *
 * Что на самом деле даёт роль, проверено по коду: `requireSuper` открывает всё
 * `/api/super/*` — управление платформой, — а `requireRole` пропускает `isSuper`
 * везде, где нужен администратор, то есть в любую подключённую организацию.
 * Больше в описании ничего и не обещаем.
 *
 * Имя — только подпись для этого списка: в Telegram оно не уходит, самому
 * человеку не показывается и на права не влияет. Сервер его не требует
 * (`name: z.string().max(120).default('')`), поэтому и здесь оно необязательно.
 *
 * Telegram ID сервер принимает любым целым (`z.number().int()`), своих пределов
 * длины не выдумываем: проверяем только то, что это цифры и что число доедет
 * без потери точности — дальше границы JSON-числа ID перестал бы быть собой.
 */
function AddSuperAdminSheet() {
  const sheet = useSheet();
  const toast = useToast();
  const owners = useSuperAdmins();
  const add = useAddSuperAdmin();
  const [tgId, setTgId] = useState('');
  const [name, setName] = useState('');
  const [idError, setIdError] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const idRef = useRef<HTMLInputElement>(null);

  // Пробелы по краям приезжают вместе со вставкой из буфера — они не ошибка ввода.
  const raw = tgId.trim();
  /**
   * Уже в списке? Спрашиваем список, а не сервер: добавление того, кто там есть,
   * сервер не отклоняет — оно возвращает права приостановленному. Значит
   * «уже добавлен» — правда только про действующего владельца.
   */
  const existing = (owners.data ?? []).find((a) => String(a.tg_id) === raw) ?? null;
  const willRestore = !!existing?.suspended_at;

  function check(): string | null {
    if (!raw) return 'Введите Telegram ID';
    if (!/^\d+$/.test(raw)) return 'Telegram ID должен содержать только цифры';
    if (!Number.isSafeInteger(Number(raw))) return 'Такого Telegram ID не бывает — проверьте номер';
    if (existing && !existing.suspended_at) return 'Этот пользователь уже добавлен в список владельцев';
    return null;
  }

  function submit() {
    const found = check();
    setIdError(found);
    setFailed(null);
    if (found) {
      idRef.current?.focus();
      return;
    }
    add.mutate({ tgId: Number(raw), name: name.trim() }, {
      onSuccess: () => {
        toast(willRestore ? 'Доступ восстановлен' : 'Владелец добавлен');
        sheet.close();
      },
      // Ошибку отправки показываем в самом окне: тост уедет вместе с ним, а
      // повторить попытку надо здесь же и теми же данными — они на месте.
      onError: (e) => setFailed(
        e instanceof ApiError ? e.message : 'Не получилось добавить. Проверьте связь и попробуйте ещё раз',
      ),
    });
  }

  return (
    <>
      <h3>Добавить владельца</h3>
      <p>Укажите Telegram ID человека, которому хотите предоставить права владельца.</p>
      <p>Владелец получит доступ к управлению платформой и подключёнными организациями.</p>

      <div className={`field${idError ? ' is-bad' : ''}`}>
        <label htmlFor="saId">Telegram ID</label>
        <input
          id="saId"
          ref={idRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Например, 123456789"
          aria-invalid={!!idError}
          aria-describedby="saIdNote"
          value={tgId}
          onChange={(e) => { setTgId(e.target.value); setIdError(null); }}
        />
        {/* Ошибка не вытесняет подсказку: подсказка отвечает на другой вопрос —
            где взять ID, — и убирать её ровно тогда, когда ID не сходится,
            значит убирать ответ в момент вопроса. */}
        <span id="saIdNote">
          {idError ? <span className="err" role="alert">{idError}</span> : null}
          <span className="hint">
            Пользователь может узнать свой ID через{' '}
            <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a>.
          </span>
          {willRestore ? (
            <span className="hint">У этого владельца доступ приостановлен — добавление вернёт его.</span>
          ) : null}
        </span>
      </div>

      <div className="field">
        <label htmlFor="saName">Имя для отображения (необязательно)</label>
        <input
          id="saName"
          type="text"
          placeholder="Например, Никита"
          aria-describedby="saNameNote"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <span className="hint" id="saNameNote">Подпись в этом списке. В Telegram не уходит.</span>
      </div>

      {failed ? <p className="err" role="alert">{failed}</p> : null}

      <div className="row-actions fitwrap">
        <button className="btn" type="button" onClick={() => sheet.close()}>Отмена</button>
        <button className="btn solid" type="button" disabled={add.isPending} onClick={submit}>
          {add.isPending ? 'Добавляем…' : 'Добавить владельца'}
        </button>
      </div>
    </>
  );
}
